import { Component } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

// Components
import { TooltipLoader } from '../../global/TooltipLoader';
import { defineMessages, useIntl } from 'dibs-react-intl';
import PrintIcon from 'dibs-icons/exports/Print';
import { Link } from 'dibs-elements/exports/Link';
import { Menu } from 'dibs-elements/exports/Menu';
import { useMenuTrigger } from 'dibs-elements/exports/useMenuTrigger';

// redux actions
import { printActions } from '../../actions/pdpRespPrintActions';

// helpers
import { trackPrintNoPriceClick, trackPrintWithPriceClick } from '../tracking/iconBubblesTracking';

import styles from './PdpRespIconBubbles.scss';

// messages
const messages = defineMessages({
    print: { id: 'PdpRespIconBubblePrint.print', defaultMessage: 'Print' },
    printNoPrice: {
        id: 'PdpRespIconBubblePrint.printNoPrice',
        defaultMessage: 'Print without Price',
    },
    printTrade: { id: 'PdpRespIconBubblePrint.printTrade', defaultMessage: 'Print Tear Sheet' },
    printNoPriceTrade: {
        id: 'PdpRespIconBubblePrint.printNoPriceTrade',
        defaultMessage: 'Print Tear Sheet without Price',
    },
});

const TOOLTIP_HIDE_TIMEOUT = 200;

const PrintMenuLink = ({ isMenuOpen, onOpen, onClose }) => {
    const intl = useIntl();
    const callbackRef = useMenuTrigger(isMenuOpen, onOpen, onClose);
    return (
        <Link
            onClick={onOpen}
            linkRef={callbackRef}
            className={styles.bubble}
            ariaLabel={intl.formatMessage(messages.print)}
        >
            <PrintIcon className={styles.icon} />
        </Link>
    );
};
PrintMenuLink.propTypes = {
    isMenuOpen: PropTypes.bool.isRequired,
    onOpen: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
};

const PrintCopy = ({ isTrade, showPrice }) => {
    const intl = useIntl();
    let message;
    if (showPrice) {
        message = isTrade ? 'printTrade' : 'print';
    } else {
        message = isTrade ? 'printNoPriceTrade' : 'printNoPrice';
    }
    return <>{intl.formatMessage(messages[message])}</>;
};
PrintCopy.propTypes = {
    isTrade: PropTypes.bool,
    showPrice: PropTypes.bool,
};

class PdpRespIconBubblePrintComponent extends Component {
    constructor(props) {
        super(props);

        this.state = { showTooltip: false };
        this.showTooltip = this.showTooltip.bind(this);
        this.hideTooltip = this.hideTooltip.bind(this);
        this.hideTooltipAfterTimeout = this.hideTooltipAfterTimeout.bind(this);
        this.onClickPrintWithPrice = this.onClickPrintWithPrice.bind(this);
        this.onClickPrintWithoutPrice = this.onClickPrintWithoutPrice.bind(this);
    }
    componentDidUpdate(prevProps) {
        if (this.props.pdpPrint.triggerWindowPrint && !prevProps.pdpPrint.triggerWindowPrint) {
            window.print();
            this.props.updateTriggerWindowPrint();
        }
    }
    showTooltip() {
        if (this.timeout) {
            clearTimeout(this.timeout);
        }
        this.setState({ showTooltip: true });
    }
    hideTooltip(cb) {
        this.setState({ showTooltip: false }, () => {
            // we want to update print with price / without price
            if (cb && typeof cb === 'function') {
                cb();
            }
        });
    }
    hideTooltipAfterTimeout() {
        this.timeout = setTimeout(() => this.hideTooltip(), TOOLTIP_HIDE_TIMEOUT);
    }
    onClickPrintWithPrice(e) {
        e.stopPropagation();
        trackPrintWithPriceClick();
        this.hideTooltip(() => this.props.updateToPrintWithPrice());
    }
    onClickPrintWithoutPrice(e) {
        e.stopPropagation();
        trackPrintNoPriceClick();
        this.hideTooltip(() => this.props.updateToPrintWithoutPrice());
    }

    render() {
        const { showTooltip } = this.state;
        const { isTrade } = this.props;
        const tradeDataTnSuffix = isTrade ? '-trade' : '';

        return (
            <div
                onMouseEnter={isTrade ? this.showTooltip : undefined}
                onMouseLeave={isTrade ? this.hideTooltipAfterTimeout : undefined}
                className={classnames(styles.tooltipWrapper, 'enable-click-on-preview')}
                data-tn="icon-bubble-print"
            >
                {/* Print Icon */}
                <PrintMenuLink
                    isMenuOpen={showTooltip}
                    onOpen={this.showTooltip}
                    onClose={this.hideTooltip}
                />
                {/* Tooltip */}
                <TooltipLoader
                    load={showTooltip}
                    isVisible={showTooltip}
                    closeOnOutsideClick
                    onClose={this.hideTooltip}
                >
                    <Menu onClose={this.hideTooltip} isOpen={showTooltip}>
                        <Link
                            role="menuitem"
                            className={styles.printLink}
                            onClick={this.onClickPrintWithPrice}
                            dataTn={`print${tradeDataTnSuffix}`}
                        >
                            <PrintCopy isTrade={isTrade} showPrice />
                        </Link>
                        <Link
                            role="menuitem"
                            className={styles.printLink}
                            onClick={this.onClickPrintWithoutPrice}
                            dataTn={`print-no-price${tradeDataTnSuffix}`}
                        >
                            <PrintCopy isTrade={isTrade} />
                        </Link>
                    </Menu>
                </TooltipLoader>
            </div>
        );
    }
}

PdpRespIconBubblePrintComponent.propTypes = {
    intl: PropTypes.object,
    pdpPrint: PropTypes.shape({
        triggerWindowPrint: PropTypes.bool.isRequired,
    }).isRequired,
    updateToPrintWithPrice: PropTypes.func.isRequired,
    updateToPrintWithoutPrice: PropTypes.func.isRequired,
    updateTriggerWindowPrint: PropTypes.func.isRequired,
    isTrade: PropTypes.bool,
};

// Redux Map Functions
function mapStateToProps({ pdpPrint }) {
    return { pdpPrint };
}
function mapDispatchToProps(dispatch) {
    return {
        updateToPrintWithPrice: () => dispatch(printActions.printWithPrice()),
        updateToPrintWithoutPrice: () => dispatch(printActions.printWithoutPrice()),
        updateTriggerWindowPrint: () => dispatch(printActions.windowPrintTriggered()),
    };
}

export const PdpRespIconBubblePrint = connect(
    mapStateToProps,
    mapDispatchToProps
)(PdpRespIconBubblePrintComponent);
